import rcss from "../../../Styles/reports.module.css";
import { observer } from "mobx-react-lite";
import { KnownTrackerView } from "./KnownTrackerView";
import { CodeBehaviorsObserved } from "../../../lib/Analysis/CodeBehavior";
import { clusterBy } from "../../../lib/utils/toMap";
import { ResultsForPageDomainCodeUrlAndDestinationDomain } from "../SearchResultsObserver";
import { ResultsForSearchTermView } from "./ResultsForOriginalSearchTermView";
import { getTrackingCodeReportState, TrackingCodeReportObserverInputs } from "../getTrackingCodeReportState";
import { InteractiveRequestIdBaseProps } from "../InteractiveRequestId";
import { GetsDbEventsByIndex } from "../../../State/EvidenceDB/DbEventCache";
import { HasCallStackResolution } from "../../../lib/Causation/CallStackResolution";
import { Literal } from "../../../Styles/ReportStyles";
import { HasLicensePermissions } from "../../../State/EvidenceDB/LicensePermissions";
import { UnlicensedText } from "../../../constants";

export const CodeBehaviorView = observer ( ({
	codeBehavior
}: {
	codeBehavior?: CodeBehaviorsObserved
}) => {
	if (codeBehavior == null) return null;
	const {geolocationsRead, keystrokesRead, globalListeners, globalPropertiesRead, canvasRenderingContexts, apiConstants,
	} = codeBehavior;
	if (geolocationsRead == null && keystrokesRead == null && globalListeners == null && globalPropertiesRead == null) return null;
	return (<>
		<h3>Code behavior</h3>
		<div className={rcss.indented}>
			{ geolocationsRead == null || geolocationsRead.length == 0 ? null : (<>
				<h4>Geolocation</h4>
				<div className={rcss.indented}>{ geolocationsRead.map( ({longitude, latitude, eventIndex}) => 
					<div key={eventIndex}>
						<Literal>{longitude}</Literal>,&#32;
						<Literal>{latitude}</Literal>
					</div>)
				}</div>
			</>)}
			{ (canvasRenderingContexts == null || canvasRenderingContexts.length === 0) && (apiConstants == null || apiConstants.length == 0) ? null : (<>
				<h4>APIs accessed (often used for fingerprinting)</h4>
				<div className={rcss.indented}>{
						canvasRenderingContexts == null || canvasRenderingContexts.length === 0 ? null : (<>
							<div><Literal>HTMLCanvasElement.getContext()</Literal> called with <Literal>{ 
								canvasRenderingContexts.map( c => c.contextId ).join(", ")}</Literal></div>
					</>)
					}{
						apiConstants == null ? null : 
							apiConstants.map( ({apiConstant, value}) => (
							<div key={apiConstant}><Literal>{apiConstant}={value}</Literal></div>
					))
				}
				</div>
			</>)}
			{ keystrokesRead == null || keystrokesRead.length === 0 ? null : (<>
				<h4>Keystrokes read (by listening to global DOM elements)</h4>
				<div className={rcss.indented}>{ keystrokesRead.map( ({keyEvent, tag, keystrokesAsString}) => 
					<div key={`$${keyEvent}\0${tag}`}>
						<Literal>{keyEvent} ({tag})</Literal>:&nbsp;
						&ldquo;<Literal>{ keystrokesAsString }</Literal>&rdquo;
					</div>)
				}</div>
			</>)}
			{ globalListeners == null || globalListeners.length === 0 ? null : (<>
				<h4>Browser (DOM) events listened upon</h4>
				<div className={rcss.indented}>{
					clusterBy(globalListeners, gl => gl.domEventName)
					.map(
						([domEventName, globalListeners], index) => (
						<span key={`${domEventName}`}>{index === 0 ? null : (<>,&nbsp;</>)}
							<span className={rcss.sizeAdjustedLowercaseLiteral}>{domEventName}</span> ({ globalListeners.map( ({tag}, index) => (
								<span key={tag}>
									{index === 0 ? null : (<>,&nbsp;</>)}
									<Literal>{tag}</Literal>
								</span>
							))})
						</span>)
					)
				}</div>	
			</>)}
			{ globalPropertiesRead == null || globalPropertiesRead.length === 0 ? null : (<>
				<h4>Browser (DOM) event properties read</h4>
				<div className={rcss.indented}>{
					clusterBy(globalPropertiesRead, g => g.domEventName )
					.map( ([event, properties]) => 
					<div className={rcss.indented} key={event}>
						<span className={rcss.sizeAdjustedLowercaseLiteral}>{event}</span>:&nbsp;
						{ clusterBy(properties, p => p.property)
						.map( ([property, tags], index) => (
							<span key={property}>{index === 0 ? null : (<>,&nbsp;</>)}
								<Literal>{property}</Literal> ({ tags.map( ({tag}, index) => (<span key={tag}>
									{index === 0 ? null : (<>,&nbsp;</>)}
									<Literal>{tag}</Literal>
								</span>))})
							</span>) ) }
					</div>)
				}</div>
			</>)}
		</div>
	</>);
});

export const CookieSubsectionView = observer( ({cookies}: {cookies?: {name: string, value: string}[]}) => {
	if (cookies == null || cookies.length === 0) return null;
	return (<>
		<h3>Cookies</h3>
			<div className={rcss.indented}>{
			cookies.map( (cookie) => (
				<div className={rcss.cookieLine} key={cookie.name}><Literal>{cookie.name}={cookie.value}</Literal></div>
		))}</div>
	</>)
});

export const DataTransmittedSubsectionView = observer( (
	{
		dataSentByDestinationDomain,
		...interactiveRequestIdBaseProps
	}: HasCallStackResolution & InteractiveRequestIdBaseProps & HasLicensePermissions & {
		getsDbEventsByIndex: GetsDbEventsByIndex;
		dataSentByDestinationDomain:  ResultsForPageDomainCodeUrlAndDestinationDomain[];
	}) => {
	if (dataSentByDestinationDomain.length === 0) return null;;
	return (<>
		<h3>Data Transmitted</h3>
			<div className={rcss.indented}>{
			dataSentByDestinationDomain.map( ({destinationDomain, bySearchTerm}) => (
				<div className={rcss.indented} key={destinationDomain}>
					<div>Sent to <Literal>{ destinationDomain }</Literal></div>
					{ bySearchTerm.map( (resultsForSearchTerm) => (
							<ResultsForSearchTermView
								key={resultsForSearchTerm.searchTermAsSpecified}
								{...{resultsForSearchTerm, ...interactiveRequestIdBaseProps}} />
						))
					}
				</div>
			))}
			</div>
	</>)
});



export const TrackingCodeReportView = observer(
	({
		searchResultsObserver, codeBehaviorsObserver, ...rest
	}: HasCallStackResolution &  InteractiveRequestIdBaseProps &
		TrackingCodeReportObserverInputs & HasLicensePermissions & {
		getsDbEventsByIndex: GetsDbEventsByIndex;
	}
) => {
		const trackingCodeReportState = getTrackingCodeReportState({searchResultsObserver, codeBehaviorsObserver});
		return (<>
			{trackingCodeReportState.map( ({ pageDomain, byCodeUrl }) => {
				const licenseRestrictionText = !rest.licensePermissions.mayUseForCommercialPurposesAt({pageDomain}) ?
					UnlicensedText : undefined;
				return (
					<main className={rcss.report} key={pageDomain} data-license={licenseRestrictionText}>
						<h1>Code Behaviors of Trackers on &#32;
							<span className={rcss.sizeAdjustedLowercaseLiteral}>{pageDomain}</span></h1>
						{byCodeUrl.map(({
							codeUrl,
							codeBehavior,
							knownTracker,
							dataSentByDestinationDomain,
						}) => {
							const organizationKnownAs = knownTracker?.organizationKnownAs;
							return (
								<section className={rcss.reportSection} key={`${codeUrl}\0${pageDomain}`}>
									<h2>
										<span className={rcss.sizeAdjustedLowercaseLiteral}>{codeUrl}</span>
										{organizationKnownAs == null ? null : (<>
												&#32;({organizationKnownAs})
										</>)
									}</h2>
									<KnownTrackerView knownTracker={knownTracker} />
									<CodeBehaviorView codeBehavior={codeBehavior} />
									<DataTransmittedSubsectionView dataSentByDestinationDomain={dataSentByDestinationDomain} {...rest} />
								</section>
						)})}
					</main>
				);
			})}
		</>);
	});

